import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const FinancialOverview = ({ selectedPeriod, isSecureView }) => {
  const [activeTab, setActiveTab] = useState('revenue');

  const revenueData = [
    { month: 'Jan', revenue: 3200000, profit: 2180000, expenses: 1020000 },
    { month: 'Feb', revenue: 3850000, profit: 2625000, expenses: 1225000 },
    { month: 'Mar', revenue: 4100000, profit: 2800000, expenses: 1300000 },
    { month: 'Apr', revenue: 3900000, profit: 2660000, expenses: 1240000 },
    { month: 'May', revenue: 4300000, profit: 2940000, expenses: 1360000 },
    { month: 'Jun', revenue: 4750000, profit: 3245000, expenses: 1505000 }
  ];

  const practiceAreaData = [
    { name: 'Corporate Law', value: 12500000, color: '#6366F1' },
    { name: 'Litigation', value: 8900000, color: '#8B5CF6' },
    { name: 'Real Estate', value: 7200000, color: '#06B6D4' },
    { name: 'Family Law', value: 5800000, color: '#10B981' },
    { name: 'Criminal Law', value: 4200000, color: '#F59E0B' },
    { name: 'Others', value: 3100000, color: '#EF4444' }
  ];

  const expenseCategories = [
    { category: 'Staff Salaries', amount: 2400000, percentage: 38.5 },
    { category: 'Office Rent', amount: 850000, percentage: 13.6 },
    { category: 'Technology', amount: 620000, percentage: 9.9 },
    { category: 'Marketing', amount: 480000, percentage: 7.7 },
    { category: 'Legal Research', amount: 350000, percentage: 5.6 },
    { category: 'Utilities', amount: 280000, percentage: 4.5 },
    { category: 'Others', amount: 1250000, percentage: 20.2 }
  ];

  const partnerDistributions = [
    { partner: 'Senior Partner A', amount: 1850000, percentage: 28.5 },
    { partner: 'Senior Partner B', amount: 1620000, percentage: 25.0 },
    { partner: 'Partner C', amount: 1350000, percentage: 20.8 },
    { partner: 'Partner D', amount: 980000, percentage: 15.1 },
    { partner: 'Junior Partner E', amount: 690000, percentage: 10.6 }
  ];

  const tabs = [
    { id: 'revenue', label: 'Revenue Trends', icon: 'TrendingUp' },
    { id: 'expenses', label: 'Expense Analysis', icon: 'PieChart' },
    { id: 'distributions', label: 'Partner Draws', icon: 'Users' }
  ];

  const formatCurrency = (value) => {
    if (!isSecureView) return 'KES ••••••••';
    return `KES ${(value / 1000000).toFixed(1)}M`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-surface border border-border rounded-lg shadow-lg p-3">
          <p className="text-sm font-medium text-text-primary">{label}</p>
          {payload?.map((entry) => (
            <p key={entry?.dataKey} className="text-sm" style={{ color: entry?.color }}>
              {entry?.name}: {formatCurrency(entry?.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-surface rounded-lg shadow-card border border-border">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-heading font-semibold text-text-primary">Financial Overview</h2>
          <div className="flex items-center space-x-2">
            {!isSecureView && (
              <div className="flex items-center space-x-1 text-warning-600">
                <Icon name="Lock" size={14} />
                <span className="text-xs">Restricted</span>
              </div>
            )}
            <button className="p-2 hover:bg-secondary-100 rounded-lg transition-smooth">
              <Icon name="Download" size={16} className="text-text-secondary" />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 bg-secondary-100 rounded-lg p-1">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-smooth ${
                activeTab === tab?.id
                  ? 'bg-surface text-primary shadow-sm'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span className="hidden sm:inline">{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="p-6">
        {/* Revenue Trends Tab */}
        {activeTab === 'revenue' && (
          <div>
            <div className="mb-6">
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center">
                  <p className="text-sm text-text-secondary">Total Revenue</p>
                  <p className="text-xl font-bold text-text-primary">{formatCurrency(25150000)}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-text-secondary">Total Profit</p>
                  <p className="text-xl font-bold text-success">{formatCurrency(17450000)}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-text-secondary">Total Expenses</p>
                  <p className="text-xl font-bold text-text-secondary">{formatCurrency(7700000)}</p>
                </div>
              </div>
            </div>

            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueData}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366F1" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#6366F1" stopOpacity={0.1}/>
                    </linearGradient>
                    <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="month" stroke="#6B7280" />
                  <YAxis stroke="#6B7280" tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="revenue" stroke="#6366F1" fillOpacity={1} fill="url(#colorRevenue)" />
                  <Area type="monotone" dataKey="profit" stroke="#10B981" fillOpacity={1} fill="url(#colorProfit)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-6">
              <h3 className="text-md font-semibold text-text-primary mb-4">Revenue by Practice Area</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={practiceAreaData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {practiceAreaData?.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry?.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-4">
                {practiceAreaData?.map((area) => (
                  <div key={area?.name} className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: area?.color }}></div>
                    <span className="text-sm text-text-secondary">{area?.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Expenses Tab */}
        {activeTab === 'expenses' && (
          <div>
            <div className="space-y-4">
              {expenseCategories?.map((expense) => (
                <div key={expense?.category} className="flex items-center justify-between p-4 bg-secondary-50 rounded-lg">
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-text-primary">{expense?.category}</h3>
                    <div className="flex items-center space-x-2 mt-1">
                      <div className="w-32 bg-secondary-200 rounded-full h-2">
                        <div
                          className="bg-primary h-2 rounded-full"
                          style={{ width: `${expense?.percentage}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-text-secondary">{expense?.percentage}%</span>
                    </div>
                  </div>
                  <div className="text-right ml-4">
                    <p className="text-sm font-semibold text-text-primary">{formatCurrency(expense?.amount)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Partner Distributions Tab */}
        {activeTab === 'distributions' && (
          <div>
            <div className="space-y-4">
              {partnerDistributions?.map((partner) => (
                <div key={partner?.partner} className="flex items-center justify-between p-4 bg-secondary-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                      <Icon name="User" size={20} color="white" />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-text-primary">{partner?.partner}</h3>
                      <div className="flex items-center space-x-2 mt-1">
                        <div className="w-24 bg-secondary-200 rounded-full h-2">
                          <div
                            className="bg-success h-2 rounded-full"
                            style={{ width: `${partner?.percentage}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-text-secondary">{partner?.percentage}%</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-text-primary">{formatCurrency(partner?.amount)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FinancialOverview;